import React, { useState } from "react";
import { Alert } from "@mui/material";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import { useNotification } from "@context/NotificationContext";
import { useBayHireRequestList } from "@features/bay-hire/hooks/useBayHireRequestList";
import {
  canCancelBayHireRequest,
  canUpdateBayHireRequest,
  canUploadBayHirePaymentReceipt,
} from "@features/bay-hire/utils/bayHireRequestActionGuards";
import {
  BAY_HIRE_BFF_SERVICE_SEGMENT,
  getBayHirePrintDocument,
  type BayHireRequest,
} from "@services/bayHireApi";
import { paths } from "@utils/navigation";
import { printRequestedTemplate } from "@utils/printRequestedTemplate";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CancelDialog from "../form/CancelDialog";
import ReceiptUploadDialog from "../form/ReceiptUploadDialog";

const BayHireRequestList: React.FC = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const handleUpdateRequest = (request: BayHireRequest) =>
    navigate(`/${paths.bayHire.request}`, { state: { request } });
  const {
    headers,
    rows,
    isLoading,
    isError,
    page,
    pageSize,
    totalRecordCount,
    handlePageChange,
    filters,
    availableFields,
    handleApplyFilters,
    handleClearFilters,
  } = useBayHireRequestList();

  // DFT-0044 slice F (F2): Cancel/Add Receipt are simple modals, not a mode switch - unlike
  // Update Request they can stay entirely local to this component. useCancelBayHireRequest /
  // useUploadBayHireReceipt (used inside these dialogs) already invalidate the `requests` query
  // key on success, so this list's own page refetches without any extra wiring here.
  const [cancelRequestId, setCancelRequestId] = useState<string | null>(null);
  const [receiptRequestId, setReceiptRequestId] = useState<string | null>(null);

  // DFT-0075: no longer an early return that replaces the whole component - that hid the
  // Filters panel (and its Clear Filters button) behind the error alert for ANY query failure,
  // leaving no recovery path for a persisted bad filter (absorbs DFT-0079's own finding).
  // Matches the already-correct, already-working pattern on BayHireOperationsQueue.tsx/
  // BayHireWaitingListQueue.tsx/BayHireHistoryQueue.tsx: the alert renders inline, above an
  // always-rendered CustomDataTable (rows defaults to [] on error - see
  // useBayHireRequestList.tsx's `data?.records ?? []` - so CustomDataTable's own NoTableEntries
  // empty state renders correctly instead of nothing).
  //
  // DFT-0026: empty state now goes through CustomDataTable's own
  // NoTableEntries (via a real title) instead of a bespoke early-return
  // Alert, matching the same migration applied to BayHireOperationsQueue/
  // BayHireWaitingListQueue. The error Alert above is unchanged.
  //
  // DFT-0041: no longer renders its own external <h3> title, and no longer
  // overrides displaySubTitle - CustomDataTable's render condition
  // (`(title || !hideFilters) && displaySubTitle`) gates its entire header
  // block, including the Filters button, on displaySubTitle. Setting it to
  // false to avoid a duplicate title (this component previously rendered
  // both its own <h3> and passed `title` to CustomDataTable) also hid the
  // Filters button entirely. Aligned with GatepassesContainer.tsx's pattern,
  // which never overrides displaySubTitle and relies on CustomDataTable's
  // own header to render the title alongside the Filters button.
  //
  // DFT-0027: row click navigates to the request-detail route (canPreview +
  // navigationLinkBase="request-details"). DFT-0041's filters wiring above is preserved.
  //
  // DFT-0044 slice F (F2): the ⋮ kebab (audit finding F2) reduces to Print / Update Request /
  // Cancel Request / Add Receipt - "View details" is redundant with the row-click navigation this
  // list already has (unlike the Operations queues, which have no other way in). Each entry's own
  // guard is shared with the detail page's header buttons (bayHireRequestActionGuards) so the two
  // surfaces cannot silently diverge on when an action is offered.
  //
  // DFT-0045: Print is first (04_Port_License_details-3.png's own kebab reads `Print | Update
  // Request | Cancel Operation`), always visible (no new authorization surface - the :print
  // endpoint reuses the existing detail-read authorization), and calls the print endpoint
  // directly by the row's id - no navigation to the detail route first, matching how
  // Gatepass/Security-Clearance's own list-context print calls work.
  return (
    <div className="relative mt-6">
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="bay-hire-request-list-error">
          {t("bay-hire.load-error")}
        </Alert>
      )}
      <LoadingOverlay loading={isLoading} testId="bay-hire-request-list-loading" />
      <CustomDataTable
        testId="bay-hire-request-list"
        title={t("bay-hire.request-list-title")}
        actionName=""
        // FE-20: the baseline's empty-state screen keeps Filters visible - opt-in, does not
        // change any other CustomDataTable consumer's behaviour.
        showHeaderWhenEmpty
        // FE-16: "You have no Bay Hire", not the generic "You don't have any {{dataName}}".
        customEmptyMessagePrefix="bay-hire.empty-state-title"
        headers={headers}
        rows={rows}
        page={page}
        pageSize={pageSize}
        totalCount={totalRecordCount}
        onPageChange={handlePageChange}
        actionColumn={{
          actionsDisplay: "menu",
          headerName: t("bay-hire.table.action"),
          actions: [
            {
              label: t("bay-hire.print-button"),
              testId: (row) => `bay-hire-list-print-${row.id}`,
              isVisible: () => true,
              onClick: (row) =>
                void printRequestedTemplate({
                  id: String((row as BayHireRequest).id),
                  service: BAY_HIRE_BFF_SERVICE_SEGMENT,
                  requestTemplateDocument: getBayHirePrintDocument,
                  notify,
                  t,
                }),
            },
            {
              label: t("bay-hire.update-request"),
              testId: (row) => `bay-hire-list-update-${row.id}`,
              isVisible: (row) => canUpdateBayHireRequest(row as BayHireRequest),
              onClick: (row) => handleUpdateRequest(row as BayHireRequest),
            },
            {
              label: t("bay-hire.cancel-request"),
              testId: (row) => `bay-hire-list-cancel-${row.id}`,
              isVisible: (row) => canCancelBayHireRequest(row as BayHireRequest),
              onClick: (row) => setCancelRequestId(String(row.id)),
            },
            {
              // DFT-0044 FE-21: the baseline's kebab entry reads `Add Receipt`
              // (06_Port_licenses_grid.png), not the drawer's own title.
              label: t("bay-hire.add-receipt-action"),
              testId: (row) => `bay-hire-list-add-receipt-${row.id}`,
              isVisible: (row) => canUploadBayHirePaymentReceipt(row as BayHireRequest),
              onClick: (row) => setReceiptRequestId(String(row.id)),
            },
          ],
        }}
        canPreview
        navigationLinkBase="request-details"
        filters={filters}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        availableFields={availableFields}
        pageSizeOptions={[10, 25, 50, 100]}
      />

      {cancelRequestId && (
        <CancelDialog
          open
          requestId={cancelRequestId}
          onClose={() => setCancelRequestId(null)}
          onSuccess={() => setCancelRequestId(null)}
        />
      )}

      {receiptRequestId && (
        <ReceiptUploadDialog
          open
          requestId={receiptRequestId}
          onClose={() => setReceiptRequestId(null)}
          onSuccess={() => setReceiptRequestId(null)}
        />
      )}
    </div>
  );
};

export default BayHireRequestList;
